/*
 * <elexa-loop-carousel>: centred, seamlessly looping transform carousel (D2 C6 "testimonials" and
 * "video slider" modes), built on the foundation's arrow and dot styles.
 *
 * Markup:
 *   <elexa-loop-carousel [swipe] [native-below="1025"] [lock="350"]>
 *     <div data-ex-loop-viewport><div data-ex-loop-track> slides </div></div>
 *     [data-ex-prev] [data-ex-next] [data-ex-dots]
 *   </elexa-loop-carousel>
 *
 * - Clones of every slide are placed before and after the originals (two sets per side when there
 *   are fewer than 4 slides). They get data-ex-clone, aria-hidden and inert, lose ids and editor
 *   attributes, and their <video>/<iframe> sources are parked in data-src until a section loads them.
 * - The active (centred) card drives the dots. After a move that lands on a clone the track jumps
 *   silently to the matching original, so the loop never rewinds.
 * - swipe: touch swipe > 50 px (horizontal and dominant) moves one card; vertical scrolling is left alone.
 * - native-below: below this viewport width the element switches to native scroll-snap (no clones
 *   shown); arrows then scroll one card and centre it.
 * - Inputs are ignored for `lock` ms after a move. Reduced motion: instant moves.
 * - Theme editor: selecting a block centres that slide.
 * Events: elexa:carousel-change { index, total } (bubbles).
 */
(() => {
  if (customElements.get('elexa-loop-carousel')) return;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rtl = (el) => getComputedStyle(el).direction === 'rtl';

  class ElexaLoopCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        requestAnimationFrame(() => this.layout(true));
        return;
      }
      this.viewport = this.querySelector('[data-ex-loop-viewport]');
      this.track = this.querySelector('[data-ex-loop-track]');
      if (!this.viewport || !this.track) return;
      this.ready = true;
      this.originals = Array.from(this.track.children).filter((el) => !el.hasAttribute('data-ex-clone'));
      this.n = this.originals.length;
      this.lockMs = parseInt(this.getAttribute('lock') || '350', 10);
      this.nativeBelow = parseInt(this.getAttribute('native-below') || '0', 10);
      this.prevButtons = Array.from(this.querySelectorAll('[data-ex-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-ex-next]'));
      this.dotsEl = this.querySelector('[data-ex-dots]');
      this.originals.forEach((el, i) => (el.dataset.exIndex = i));

      if (this.n <= 1) {
        this.setAttribute('static', '');
        this.pos = 0;
        this.prevButtons.concat(this.nextButtons).forEach((b) => (b.disabled = true));
        this.bindResize();
        this.layout(true);
        return;
      }

      const sets = this.n < 4 ? 2 : 1;
      const before = document.createDocumentFragment();
      for (let s = 0; s < sets; s++) this.originals.forEach((el, i) => before.appendChild(this.makeClone(el, i)));
      const after = document.createDocumentFragment();
      for (let s = 0; s < sets; s++) this.originals.forEach((el, i) => after.appendChild(this.makeClone(el, i)));
      this.track.insertBefore(before, this.track.firstChild);
      this.track.appendChild(after);
      this.offset = sets * this.n;
      this.pos = this.offset;

      this.buildDots();
      this.prevButtons.forEach((b) => b.addEventListener('click', (e) => (e.preventDefault(), this.move(-1))));
      this.nextButtons.forEach((b) => b.addEventListener('click', (e) => (e.preventDefault(), this.move(1))));
      if (this.hasAttribute('swipe')) this.bindSwipe();
      this.viewport.addEventListener('scroll', () => {
        if (!this.isNative) return;
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      }, { passive: true });
      this.addEventListener('shopify:block:select', (e) => {
        const slide = this.originals.find((s) => s === e.target || s.contains(e.target));
        if (slide) this.goTo(parseInt(slide.dataset.exIndex, 10), true);
      });
      this.bindResize();
      this.layout(true);
    }

    makeClone(el, i) {
      const c = el.cloneNode(true);
      c.setAttribute('data-ex-clone', '');
      c.setAttribute('aria-hidden', 'true');
      c.inert = true;
      c.dataset.exIndex = i;
      c.removeAttribute('id');
      c.removeAttribute('data-shopify-editor-block');
      c.querySelectorAll('[id]').forEach((x) => x.removeAttribute('id'));
      c.querySelectorAll('[data-shopify-editor-block]').forEach((x) => x.removeAttribute('data-shopify-editor-block'));
      c.querySelectorAll('video').forEach((v) => {
        v.pause && v.pause();
        v.removeAttribute('autoplay');
        if (v.getAttribute('src')) {
          v.dataset.src = v.getAttribute('src');
          v.removeAttribute('src');
        }
        v.querySelectorAll('source[src]').forEach((s) => {
          s.dataset.src = s.getAttribute('src');
          s.removeAttribute('src');
        });
        v.preload = 'none';
        delete v.dataset.exLoaded;
      });
      c.querySelectorAll('iframe[src]').forEach((f) => {
        f.dataset.src = f.getAttribute('src');
        f.removeAttribute('src');
      });
      return c;
    }

    get isNative() {
      return this.nativeBelow > 0 && window.innerWidth < this.nativeBelow;
    }

    all() {
      return Array.from(this.track.children);
    }

    get index() {
      if (this.n <= 1) return 0;
      if (this.isNative) return this.nativeIndex();
      return (((this.pos - this.offset) % this.n) + this.n) % this.n;
    }

    bindResize() {
      let t;
      const ro = new ResizeObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => this.layout(true), 50);
      });
      ro.observe(this.viewport);
      if (this.nativeBelow) {
        const mq = window.matchMedia(`(max-width: ${this.nativeBelow - 1}px)`);
        mq.addEventListener('change', () => {
          const idx = this.lastIndex || 0;
          if (this.isNative) {
            this.layout(true);
            this.scrollToSlide(this.originals[idx], true);
          } else {
            this.pos = (this.offset || 0) + idx;
            this.layout(true);
          }
        });
      }
    }

    layout(instant) {
      if (this.isNative) {
        this.classList.add('is-native');
        this.track.style.transform = '';
        this.update();
        return;
      }
      this.classList.remove('is-native');
      const slide = this.all()[this.pos];
      if (!slide) return;
      const vw = this.viewport.clientWidth;
      const x = vw / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
      if (instant) this.track.classList.add('is-instant');
      this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      if (instant) {
        void this.track.offsetWidth;
        this.track.classList.remove('is-instant');
      }
      this.update();
    }

    move(delta) {
      if (this.n <= 1) return;
      if (this.isNative) {
        const i = Math.max(0, Math.min(this.n - 1, this.nativeIndex() + delta));
        this.scrollToSlide(this.originals[i]);
        return;
      }
      if (this.locked) return;
      this.locked = true;
      setTimeout(() => (this.locked = false), this.lockMs);
      this.pos += delta;
      const instant = reduced();
      this.layout(instant);
      clearTimeout(this.fixTimer);
      this.fixTimer = setTimeout(() => this.normalise(), instant ? 10 : this.lockMs);
    }

    normalise() {
      if (this.pos >= this.offset && this.pos < this.offset + this.n) return;
      const leaving = this.all()[this.pos];
      if (leaving) leaving.querySelectorAll('video').forEach((v) => v.pause && v.pause());
      this.pos = this.offset + ((((this.pos - this.offset) % this.n) + this.n) % this.n);
      this.layout(true);
    }

    goTo(i, instant = false) {
      if (this.n <= 1) return;
      if (this.isNative) {
        this.scrollToSlide(this.originals[i], instant);
        return;
      }
      this.pos = this.offset + i;
      this.layout(instant || reduced());
    }

    scrollToSlide(slide, instant = false) {
      if (!slide) return;
      const behavior = instant || reduced() ? 'instant' : 'smooth';
      if (rtl(this)) {
        slide.scrollIntoView({ inline: 'center', block: 'nearest', behavior });
        return;
      }
      const left = slide.offsetLeft - (this.viewport.clientWidth - slide.offsetWidth) / 2;
      this.viewport.scrollTo({ left: Math.max(0, left), behavior });
    }

    nativeIndex() {
      const box = this.viewport.getBoundingClientRect();
      const centre = box.left + box.width / 2;
      let best = 0;
      let dist = Infinity;
      this.originals.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - centre);
        if (d < dist) {
          dist = d;
          best = i;
        }
      });
      return best;
    }

    buildDots() {
      if (!this.dotsEl) return;
      this.dotsEl.innerHTML = '';
      const strings = (window.Elexa && window.Elexa.config && window.Elexa.config.strings) || {};
      const tpl = strings.goToSlide || 'Go to slide [index]';
      for (let i = 0; i < this.n; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ex-dot';
        dot.setAttribute('aria-label', tpl.replace('[index]', i + 1));
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsEl.appendChild(dot);
      }
    }

    update() {
      const index = this.index;
      const active = this.isNative ? this.originals[index] : this.all()[this.pos];
      this.all().forEach((s) => s.classList.toggle('is-active', s === active));
      if (this.dotsEl) {
        Array.from(this.dotsEl.children).forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
      }
      if (index !== this.lastIndex) {
        this.lastIndex = index;
        this.dispatchEvent(new CustomEvent('elexa:carousel-change', { bubbles: true, detail: { index, total: this.n } }));
      }
    }

    bindSwipe() {
      let x0 = 0;
      let y0 = 0;
      let dir = null;
      let dx = 0;
      this.viewport.addEventListener('touchstart', (e) => {
        if (this.isNative || e.touches.length !== 1) return;
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
        dx = 0;
        dir = null;
      }, { passive: true });
      this.viewport.addEventListener('touchmove', (e) => {
        if (this.isNative || e.touches.length !== 1) return;
        dx = e.touches[0].clientX - x0;
        const dy = e.touches[0].clientY - y0;
        if (dir === null) {
          if (Math.abs(dx) > 20 && Math.abs(dx) > Math.abs(dy)) dir = 'x';
          else if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) dir = 'y';
        }
        if (dir === 'x' && e.cancelable) e.preventDefault();
      }, { passive: false });
      this.viewport.addEventListener('touchend', () => {
        if (this.isNative || dir !== 'x') return;
        if (Math.abs(dx) > 50) {
          const sign = rtl(this) ? -1 : 1;
          this.move(dx * sign < 0 ? 1 : -1);
        }
        dir = null;
      });
    }
  }

  customElements.define('elexa-loop-carousel', ElexaLoopCarousel);
})();
